/**
 * categoryBoardState.ts
 *
 * The pure rules behind CategoryBoard: where each record sits, how a drag
 * changes that, and what a drop must save. Kept free of React and dnd-kit so
 * the rules are tested without a browser.
 *
 * A board is an ordered list of category ids plus, per container, the ordered
 * record ids inside it. Containers are keyed by `String(categoryId)`, with one
 * extra `UNCATEGORIZED` bucket for records that have no category.
 *
 * Uncategorized is a source, not a target: a record can be dragged out of it
 * into a real category, but nothing can be dropped into it. Saving "no
 * category" would write category 0, which other tools read as unmapped.
 */

export const UNCATEGORIZED = 'none';

export type ContainerKey = string;

export interface BoardState {
  categoryIds: number[];
  records: Record<ContainerKey, number[]>;
}

export interface RecordSave {
  categoryId: number;
  /** The target category's full list, in its new order. */
  ids: number[];
}

export function buildBoardState(
  categoryIds: number[],
  recordsByCategory: Record<number, number[]>,
  uncategorized: number[],
): BoardState {
  const records: Record<ContainerKey, number[]> = {};
  for (const id of categoryIds) records[String(id)] = [...(recordsByCategory[id] ?? [])];
  records[UNCATEGORIZED] = [...uncategorized];
  return { categoryIds: [...categoryIds], records };
}

export function containerOf(state: BoardState, recordId: number): ContainerKey | null {
  for (const [key, ids] of Object.entries(state.records)) {
    if (ids.includes(recordId)) return key;
  }
  return null;
}

/**
 * Puts the record at `toIndex` in `toKey`, taking it out of wherever it was.
 * A drop into Uncategorized, or into an unknown container, changes nothing.
 */
export function moveRecord(state: BoardState, recordId: number, toKey: ContainerKey, toIndex: number): BoardState {
  if (toKey === UNCATEGORIZED || !(toKey in state.records)) return state;
  const fromKey = containerOf(state, recordId);
  if (fromKey === null) return state;

  const records = { ...state.records };
  records[fromKey] = records[fromKey].filter((id) => id !== recordId);

  const target = fromKey === toKey ? records[fromKey] : [...records[toKey]];
  const index = Math.max(0, Math.min(toIndex, target.length));
  target.splice(index, 0, recordId);
  records[toKey] = target;

  return { ...state, records };
}

/**
 * What a finished record drag must save: the category the record ended in,
 * with that category's whole list. Null when there is nothing to save — the
 * record ended where it started, or it is still uncategorized.
 */
export function recordSave(before: BoardState, after: BoardState, recordId: number): RecordSave | null {
  const toKey = containerOf(after, recordId);
  if (toKey === null || toKey === UNCATEGORIZED) return null;

  const fromKey = containerOf(before, recordId);
  const sameSpot =
    fromKey === toKey && before.records[toKey].indexOf(recordId) === after.records[toKey].indexOf(recordId);
  if (sameSpot) return null;

  return { categoryId: Number(toKey), ids: [...after.records[toKey]] };
}

/**
 * True for a placeholder "separator" title: three or more hyphens in a row
 * ("--------", "--- Injectables ---"). Legacy screens use such categories,
 * groups and records as visual dividers; the admin boards never show them.
 */
export function isSeparatorTitle(title: string | null | undefined): boolean {
  return /-{3,}/.test(title ?? '');
}

/** Alphabetical by title, ignoring case (the order categories are listed in). */
export function compareTitles(a: string | null | undefined, b: string | null | undefined): number {
  return (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });
}
